import {
  Box,
  Typography,
  Grid,
  Button,
  Avatar,
  TextField,
  Chip,
  Card,
  CardContent,
  IconButton,
  Snackbar,
  Alert,
  Divider,
} from '@mui/material';
import { useState, type ChangeEvent } from 'react';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import { SectionCard } from '../../components/common';

interface Medication {
  id: string;
  name: string;
  dosage: string;
  frequency: string;
}

interface PatientProfile {
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  sexAtBirth: string;
  genderIdentity: string;
  pronouns: string;
  email: string;
  phone: string;
  emergencyContactName: string;
  emergencyContactPhone: string;
  addressLine1: string;
  city: string;
  state: string;
  zipCode: string;
  preferredLanguage: string;
  foodAllergies: string;
  dietaryPattern: string;
  dislikedFoods: string;
  preferredFoods: string;
  hydrationGoalLiters: string;
  weeklyExerciseGoal: string;
  hba1cGoal: string;
  weightGoal: string;
  nutritionGoalNotes: string;
}

const initialProfile: PatientProfile = {
  firstName: 'Sarah',
  lastName: 'Johnson',
  dateOfBirth: '1988-04-12',
  sexAtBirth: 'Female',
  genderIdentity: 'Female',
  pronouns: 'She/Her',
  email: 'sarah.johnson@example.com',
  phone: '(555) 231-1001',
  emergencyContactName: 'David Johnson',
  emergencyContactPhone: '(555) 231-1002',
  addressLine1: '123 Wellness Ave',
  city: 'Orlando',
  state: 'FL',
  zipCode: '32801',
  preferredLanguage: 'English',
  foodAllergies: 'Tree nuts',
  dietaryPattern: 'Mediterranean-leaning, carb-aware',
  dislikedFoods: 'Liver, sardines',
  preferredFoods: 'Greek yogurt, salmon, berries, lentils',
  hydrationGoalLiters: '2.2',
  weeklyExerciseGoal: '150 minutes/week',
  hba1cGoal: '6.8%',
  weightGoal: 'Maintain current weight',
  nutritionGoalNotes: 'Reduce post-dinner glucose spikes and improve meal consistency.',
};

export const PatientProfilePage = () => {
  const [profile, setProfile] = useState<PatientProfile>(initialProfile);
  const [profilePhoto, setProfilePhoto] = useState<string | null>(null);

  const [comorbidities, setComorbidities] = useState<string[]>([
    'Type 2 Diabetes',
    'Hypertension',
  ]);
  const [newComorbidity, setNewComorbidity] = useState('');

  const [medications, setMedications] = useState<Medication[]>([
    { id: 'med-1', name: 'Metformin', dosage: '500 mg', frequency: 'Twice daily' },
    { id: 'med-2', name: 'Lisinopril', dosage: '10 mg', frequency: 'Daily' },
  ]);
  const [newMedication, setNewMedication] = useState({
    name: '',
    dosage: '',
    frequency: '',
  });

  const [saveSuccessOpen, setSaveSuccessOpen] = useState(false);

  const handlePhotoUpload = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }
    const objectUrl = URL.createObjectURL(file);
    setProfilePhoto(objectUrl);
  };

  const handleProfileFieldChange = (field: keyof PatientProfile, value: string) => {
    setProfile((prev) => ({ ...prev, [field]: value }));
  };

  const handleAddComorbidity = () => {
    const value = newComorbidity.trim();
    if (!value || comorbidities.includes(value)) {
      return;
    }
    setComorbidities((prev) => [...prev, value]);
    setNewComorbidity('');
  };

  const handleRemoveComorbidity = (value: string) => {
    setComorbidities((prev) => prev.filter((item) => item !== value));
  };

  const handleAddMedication = () => {
    if (!newMedication.name.trim() || !newMedication.dosage.trim() || !newMedication.frequency.trim()) {
      return;
    }
    const item: Medication = {
      id: `med-${Date.now()}`,
      name: newMedication.name.trim(),
      dosage: newMedication.dosage.trim(),
      frequency: newMedication.frequency.trim(),
    };
    setMedications((prev) => [...prev, item]);
    setNewMedication({ name: '', dosage: '', frequency: '' });
  };

  const handleRemoveMedication = (id: string) => {
    setMedications((prev) => prev.filter((item) => item.id !== id));
  };

  const handleSaveProfile = () => {
    setSaveSuccessOpen(true);
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Patient Profile
      </Typography>
      <Typography variant="body1" color="text.secondary" paragraph>
        Manage your photo, health history, medications, preferences, and nutrition goals.
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <SectionCard title="Profile Photo">
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
              <Avatar src={profilePhoto ?? undefined} sx={{ width: 120, height: 120 }}>
                {profile.firstName[0]}
              </Avatar>
              <Button component="label" variant="outlined" startIcon={<PhotoCameraIcon />}>
                Add Picture
                <input type="file" accept="image/*" hidden onChange={handlePhotoUpload} />
              </Button>
            </Box>
          </SectionCard>
        </Grid>

        <Grid item xs={12} md={8}>
          <SectionCard title="Basic Information">
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="First Name" value={profile.firstName} onChange={(e) => handleProfileFieldChange('firstName', e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Last Name" value={profile.lastName} onChange={(e) => handleProfileFieldChange('lastName', e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Date of Birth" type="date" InputLabelProps={{ shrink: true }} value={profile.dateOfBirth} onChange={(e) => handleProfileFieldChange('dateOfBirth', e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Sex At Birth" value={profile.sexAtBirth} onChange={(e) => handleProfileFieldChange('sexAtBirth', e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Gender Identity" value={profile.genderIdentity} onChange={(e) => handleProfileFieldChange('genderIdentity', e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Pronouns" value={profile.pronouns} onChange={(e) => handleProfileFieldChange('pronouns', e.target.value)} />
              </Grid>
            </Grid>
          </SectionCard>
        </Grid>

        <Grid item xs={12}>
          <SectionCard title="Contact & Address">
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Email" value={profile.email} onChange={(e) => handleProfileFieldChange('email', e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Phone" value={profile.phone} onChange={(e) => handleProfileFieldChange('phone', e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Emergency Contact Name" value={profile.emergencyContactName} onChange={(e) => handleProfileFieldChange('emergencyContactName', e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Emergency Contact Phone" value={profile.emergencyContactPhone} onChange={(e) => handleProfileFieldChange('emergencyContactPhone', e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Address" value={profile.addressLine1} onChange={(e) => handleProfileFieldChange('addressLine1', e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField fullWidth label="City" value={profile.city} onChange={(e) => handleProfileFieldChange('city', e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={1.5}>
                <TextField fullWidth label="State" value={profile.state} onChange={(e) => handleProfileFieldChange('state', e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={1.5}>
                <TextField fullWidth label="Zip" value={profile.zipCode} onChange={(e) => handleProfileFieldChange('zipCode', e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Preferred Language" value={profile.preferredLanguage} onChange={(e) => handleProfileFieldChange('preferredLanguage', e.target.value)} />
              </Grid>
            </Grid>
          </SectionCard>
        </Grid>

        <Grid item xs={12}>
          <SectionCard title="Comorbidities">
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <TextField
                fullWidth
                label="Add Comorbidity"
                value={newComorbidity}
                onChange={(e) => setNewComorbidity(e.target.value)}
              />
              <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddComorbidity}>
                Add
              </Button>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {comorbidities.map((item) => (
                <Chip
                  key={item}
                  label={item}
                  onDelete={() => handleRemoveComorbidity(item)}
                  color="warning"
                  variant="outlined"
                />
              ))}
            </Box>
          </SectionCard>
        </Grid>

        <Grid item xs={12}>
          <SectionCard title="Current Medications">
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Medication Name"
                  value={newMedication.name}
                  onChange={(e) => setNewMedication((prev) => ({ ...prev, name: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Dosage"
                  value={newMedication.dosage}
                  onChange={(e) => setNewMedication((prev) => ({ ...prev, dosage: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  label="Frequency"
                  value={newMedication.frequency}
                  onChange={(e) => setNewMedication((prev) => ({ ...prev, frequency: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12} sm={1}>
                <Button fullWidth variant="contained" sx={{ height: '100%' }} onClick={handleAddMedication}>
                  <AddIcon />
                </Button>
              </Grid>
            </Grid>

            <Grid container spacing={2}>
              {medications.map((item) => (
                <Grid item xs={12} md={6} key={item.id}>
                  <Card variant="outlined">
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box>
                          <Typography variant="subtitle1" fontWeight={600}>{item.name}</Typography>
                          <Typography variant="body2" color="text.secondary">{item.dosage} • {item.frequency}</Typography>
                        </Box>
                        <IconButton color="error" onClick={() => handleRemoveMedication(item.id)}>
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </SectionCard>
        </Grid>

        <Grid item xs={12}>
          <SectionCard title="Food Preferences & Nutrition Goals">
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField fullWidth multiline minRows={2} label="Food Allergies" value={profile.foodAllergies} onChange={(e) => handleProfileFieldChange('foodAllergies', e.target.value)} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth multiline minRows={2} label="Dietary Pattern" value={profile.dietaryPattern} onChange={(e) => handleProfileFieldChange('dietaryPattern', e.target.value)} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth multiline minRows={2} label="Preferred Foods" value={profile.preferredFoods} onChange={(e) => handleProfileFieldChange('preferredFoods', e.target.value)} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth multiline minRows={2} label="Disliked Foods" value={profile.dislikedFoods} onChange={(e) => handleProfileFieldChange('dislikedFoods', e.target.value)} />
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth label="Hydration Goal (L/day)" value={profile.hydrationGoalLiters} onChange={(e) => handleProfileFieldChange('hydrationGoalLiters', e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth label="Weekly Exercise Goal" value={profile.weeklyExerciseGoal} onChange={(e) => handleProfileFieldChange('weeklyExerciseGoal', e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth label="HbA1c Goal" value={profile.hba1cGoal} onChange={(e) => handleProfileFieldChange('hba1cGoal', e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Weight Goal" value={profile.weightGoal} onChange={(e) => handleProfileFieldChange('weightGoal', e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  label="Goal Notes"
                  value={profile.nutritionGoalNotes}
                  onChange={(e) => handleProfileFieldChange('nutritionGoalNotes', e.target.value)}
                />
              </Grid>
            </Grid>
          </SectionCard>
        </Grid>

        <Grid item xs={12}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSaveProfile}>
              Save Profile
            </Button>
          </Box>
        </Grid>
      </Grid>

      <Snackbar
        open={saveSuccessOpen}
        autoHideDuration={2500}
        onClose={() => setSaveSuccessOpen(false)}
      >
        <Alert severity="success" onClose={() => setSaveSuccessOpen(false)}>
          Profile updated successfully.
        </Alert>
      </Snackbar>
    </Box>
  );
};
